import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { Suspense } from "react";
import { z } from "zod";
import {
  auditLogFilterOptionsQueryOptions,
  auditLogsQueryOptions,
} from "@/app/audit-logs/api/get-audit-logs";
import { AuditLogsSkeleton } from "@/app/audit-logs/components/audit-logs-skeleton";
import { AuditLogsList } from "@/app/audit-logs/list";
import { DATE_RANGE_VALUES } from "@/app/audit-logs/utils/date-range";
import {
  AUDIT_ACTIONS,
  AUDIT_SOURCES,
  type AuditAction,
  type AuditSource,
  RESOURCE_TYPES,
  type ResourceType,
} from "@/app/audit-logs/utils/enum-helpers";
import { PROJECT } from "@/app/capabilities/permissions";
import { requireProjectPermission } from "@/lib/route-guards";

// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
const auditActionValues = Object.values(AUDIT_ACTIONS) as [
  AuditAction,
  ...AuditAction[],
];
// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
const auditSourceValues = Object.values(AUDIT_SOURCES) as [
  AuditSource,
  ...AuditSource[],
];
// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
const resourceTypeValues = Object.values(RESOURCE_TYPES) as [
  ResourceType,
  ...ResourceType[],
];

const auditLogsSearchSchema = z.object({
  actions: fallback(z.array(z.enum(auditActionValues)), []).default([]),
  sources: fallback(z.array(z.enum(auditSourceValues)), []).default([]),
  resourceTypes: fallback(z.array(z.enum(resourceTypeValues)), []).default([]),
  dateRange: fallback(z.enum(DATE_RANGE_VALUES), "all").default("all"),
});

export type AuditLogsSearch = z.infer<typeof auditLogsSearchSchema>;

const defaultValues: AuditLogsSearch = {
  actions: [],
  sources: [],
  resourceTypes: [],
  dateRange: "all",
};

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/audit-logs"
)({
  validateSearch: zodValidator(auditLogsSearchSchema),
  search: {
    middlewares: [stripSearchParams(defaultValues)],
  },
  component: () => (
    <Suspense fallback={<AuditLogsSkeleton />}>
      <AuditLogsList />
    </Suspense>
  ),
  beforeLoad: async (ctx) => {
    await requireProjectPermission(ctx, PROJECT.auditLogs.read);
    const { organizationId, projectId } = ctx.params;
    const { actions, sources, resourceTypes, dateRange } = ctx.search;

    ctx.context.queryClient.prefetchInfiniteQuery(
      auditLogsQueryOptions({
        organizationId,
        projectId,
        actions,
        sources,
        resourceTypes,
        dateRange,
      })
    );

    ctx.context.queryClient.prefetchQuery(
      auditLogFilterOptionsQueryOptions({
        organizationId,
        projectId,
        actions,
        sources,
        resourceTypes,
        dateRange,
      })
    );
  },
  loader: () => ({ crumb: "Audit Logs" }),
});
